/**
 * Changelog entries — add a new entry at the top for each release.
 * type: "new" | "improved" | "fixed"
 */
type ChangeType = "new" | "improved" | "fixed";

interface ChangelogChange {
  type: ChangeType;
  text: string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  highlights: string;
  changes: ChangelogChange[];
}

const changelog: ChangelogEntry[] = [
  {
    version: "1.29.0",
    date: "2026-07-23",
    highlights: "CloudWatch log viewer on platform health",
    changes: [
      { type: "new", text: "CloudWatch logs — the Phoenix platform health page now has a Logs tab. Select a log group, choose a time range (1 h to 7 d), add an optional filter pattern, and browse log events in a live scrollable table." },
      { type: "improved", text: "Scheduled reports can now be delivered to a specific Slack channel directly from the schedule modal." },
      { type: "fixed", text: "A duplicate migration numbering conflict was causing the backend to crash on startup after a recent deploy. Fixed." },
    ],
  },
  {
    version: "1.28.0",
    date: "2026-07-22",
    highlights: "Phoenix & Kraken platform health, voice chat input & user table search",
    changes: [
      { type: "new", text: "Multi-platform monitoring — the platform health page now has an Overview showing Phoenix and Kraken side-by-side with KPIs and active incidents. Click either platform to drill into the full detail view with its own Incidents and Sentry tabs." },
      { type: "new", text: "Per-platform Sentry configuration — super admins can configure separate Sentry credentials and project slugs for each platform in Admin → Sentry. Multiple projects per platform supported (e.g. kraken-frontend and kraken-backend as separate chips)." },
      { type: "new", text: "Voice input in chat — click the microphone button to dictate your message. Transcript appends to existing text. Available in Chrome, Edge, and Safari." },
      { type: "new", text: "Admin users table — search by name or email in real time, click any column header to sort, and navigate pages of 20 users at a time." },
      { type: "improved", text: "Monitored URLs in the status admin can now be assigned to Phoenix or Kraken so uptime checks and incidents appear in the correct platform view." },
      { type: "improved", text: "Zendesk brand names are now included in agent tool responses so the AI can refer to brands by name rather than numeric ID." },
      { type: "improved", text: "Service status alert emails are now opt-in — existing users have been opted out and can re-enable alerts in Settings → Profile." },
      { type: "fixed", text: "Platform health Overview page was returning a 500 error due to a timezone mismatch in the backend. Fixed." },
    ],
  },
  {
    version: "1.27.0",
    date: "2026-07-22",
    highlights: "Zendesk integrations page, incidents table improvements & smarter widget errors",
    changes: [
      { type: "new", text: "Zendesk now appears on the admin Integrations page under a new Customer Support category" },
      { type: "improved", text: "Platform health incidents table is now wider and scrolls horizontally so all columns are always accessible" },
      { type: "improved", text: "Widgets affected by a gateway timeout now show a clear 'data temporarily unavailable' message instead of a generic error — helpful in staging environments where recent data may not yet be loaded" },
      { type: "fixed", text: "Inactive Zendesk brands are now hidden from the brand mappings page" },
    ],
  },
  {
    version: "1.26.0",
    date: "2026-07-20",
    highlights: "Team library defaults, dedicated user edit page & Sentry analysis improvements",
    changes: [
      { type: "new", text: "Library dashboards as team defaults — when editing a team, admins can assign library (shared) dashboards as defaults alongside scheduled dashboards; they are automatically forked into each member's intelligence home when they join" },
      { type: "new", text: "Dedicated user edit page — editing a user now opens a full bookmarkable page at /admin/users/:id with separate cards for user details and password reset, replacing the previous modal" },
      { type: "fixed", text: "Sentry 'Analyse with Brian' no longer times out — analysis now runs as a background job and the UI polls for results, eliminating the Cloudflare 524 proxy timeout that occurred on large queries" },
      { type: "fixed", text: "Sentry analysis output now renders as structured sections and a data table instead of raw markdown text" },
      { type: "fixed", text: "Library dashboard fork now persists after page refresh — the workspace assignment was being silently ignored; both the backend and frontend hook have been corrected" },
    ],
  },
  {
    version: "1.25.0",
    date: "2026-07-16",
    highlights: "Workspaces, dashboard library & teams",
    changes: [
      { type: "new", text: "Workspaces — organise your intelligence home dashboards into named workspaces; create, rename, and delete workspaces with full control over which dashboards live where" },
      { type: "new", text: "Dashboard Library — share any dashboard to the library so colleagues can discover and fork it into their own home with a single click; forked copies are fully independent" },
      { type: "new", text: "Scheduled dashboards in the intelligence home — scheduled reports you have access to appear as tabs alongside your personal dashboards and can be moved into any workspace" },
      { type: "new", text: "Teams — admins can create teams, manage members, and assign default dashboards that appear automatically for every member of the team" },
      { type: "new", text: "2FA grace period — users who have not set up two-factor authentication now see a dismissible notice with a 7-day deadline before 2FA is enforced" },
      { type: "improved", text: "Workspace selector and Dashboard Library button merged into the dashboard tab bar, saving a full row of screen space" },
      { type: "improved", text: "Intelligence home greeting moved to the top navigation bar; dashboard footer now shows tile count, creation date, last updated date, and the dashboard author" },
      { type: "improved", text: "Brian AI chat accessed via a sparkles icon button in the toolbar rather than a permanently visible input — opens on demand and closes when switching tabs" },
      { type: "improved", text: "New dashboard modal lets you share directly to the Dashboard Library at creation time; Brian now generates up to 12 tiles rather than being capped at 3" },
      { type: "improved", text: "Dashboard Library enhanced with full-text search, filters by workspace, pagination, and a widget preview so you can see what's inside before forking" },
      { type: "improved", text: "Empty workspaces and tab rows show dashed 'Create your first workspace / dashboard' prompts instead of a bare plus button" },
      { type: "improved", text: "New dashboards created while a workspace is active are automatically assigned to that workspace" },
      { type: "improved", text: "Trending questions admin view now shows questions from all users instead of only the current admin's own questions" },
      { type: "improved", text: "Frontend codebase fully migrated to TypeScript — all .jsx and .js files converted to .tsx and .ts for improved type safety" },
      { type: "fixed", text: "Workspace creation uses a proper in-app modal instead of the browser's native prompt()" },
      { type: "fixed", text: "Workspace delete confirmation modal now appears correctly in all cases; tab context menus no longer appear beneath other UI layers" },
      { type: "fixed", text: "Refresh token expiry reduced from 30 days to 5 days to align with security policy" },
    ],
  },
  {
    version: "1.24.0",
    date: "2026-07-15",
    highlights: "Slack bot & incident management",
    changes: [
      { type: "new", text: "Slack bot DM integration — users with the Slack Bot permission can chat with the AI agent directly via Slack DMs; responses mirror the full in-app experience" },
      { type: "new", text: "Link your Slack account from profile settings — users can enter their Slack member ID in Profile & settings without needing an admin, as long as their role has Slack Bot access" },
      { type: "new", text: "Manual incident resolution & deletion — admins can now resolve ongoing incidents or delete incident records directly from the Incidents tab on the Service Status page" },
      { type: "fixed", text: "Status check runs were silently failing every 5 minutes due to a timezone mismatch when resolving incidents with legacy naive datetimes stored in the database" },
    ],
  },
  {
    version: "1.23.0",
    date: "2026-07-14",
    highlights: "Slack alerts for service status",
    changes: [
      { type: "new", text: "Slack notifications for service incidents — a formatted message is posted to configured channels when a service goes offline or recovers, alongside the existing email alert" },
      { type: "improved", text: "Slack channel IDs are managed via the admin settings panel; only the bot token needs to be in infrastructure secrets" },
    ],
  },
  {
    version: "1.22.0",
    date: "2026-07-14",
    highlights: "Service status email alerts",
    changes: [
      { type: "new", text: "Automated email alerts for super admins when a monitored service or URL goes offline or recovers — includes service name, error detail, and downtime duration" },
      { type: "new", text: "Per-user alert opt-out — super admins can disable status alert emails from Settings → Profile without affecting other users" },
    ],
  },
  {
    version: "1.21.0",
    date: "2026-07-14",
    highlights: "Incident tracking & QA test alerts",
    changes: [
      { type: "new", text: "Incident log on the Service Status page — downtime events are automatically recorded when a service fails and resolved when it recovers, with start time, duration, and error detail" },
      { type: "new", text: "Incidents tab with filtering by service name, status (ongoing/resolved), and date range — ongoing incidents show a live pulsing badge" },
      { type: "new", text: "QA-only test alert button next to each monitored service — fires a real email and Slack alert without waiting for an actual outage" },
      { type: "fixed", text: "Service status 'last checked' timestamps were showing incorrectly due to naive UTC datetimes not being parsed as UTC on the frontend" },
    ],
  },
  {
    version: "1.20.0",
    date: "2026-07-14",
    highlights: "Roles, permissions & data access",
    changes: [
      { type: "new", text: "Permissions admin tab — super admins can configure which features (Chat, Reports, Dashboards, Service Status) are available to each role, with changes taking effect immediately" },
      { type: "new", text: "Data access groups — create named groups that restrict which datasources users can query (e.g. Phoenix Only, Kraken Only, Full Access), and assign them per user in the Users tab" },
      { type: "improved", text: "Feature-gated routing — users without access to a feature are redirected rather than seeing an error page, and the feature is hidden from the sidebar entirely" },
    ],
  },
  {
    version: "1.19.1",
    date: "2026-07-14",
    highlights: "Navigation & profile polish",
    changes: [
      { type: "improved", text: "Sidebar navigation redesigned — My Brian section is now at the top, recent chats renamed to Chats and capped at 5, with a More button for the full list" },
      { type: "new", text: "All chats modal — browse every conversation in a paginated table with search and sortable Title, Last updated, and Created columns, with inline delete" },
      { type: "improved", text: "Role badge moved from the top bar into the Profile section of Settings" },
    ],
  },
  {
    version: "1.19.0",
    date: "2026-07-14",
    highlights: "Service Status monitoring",
    changes: [
      { type: "new", text: "Service Status page — a dedicated page in the main nav shows real-time health for all platform integrations (PostgreSQL, Claude AI, Databricks)" },
      { type: "new", text: "URL uptime monitoring — admins can register any HTTP endpoint; checks run automatically every 5 minutes and report healthy, degraded (slow), or down" },
      { type: "new", text: "Status admin settings — manage monitored URLs and trigger an immediate check from the Admin panel under the new Status tab" },
      { type: "improved", text: "Overall system health banner summarises all checks at a glance — operational, degraded, or outage" },
    ],
  },
  {
    version: "1.18.1",
    date: "2026-07-14",
    highlights: "Dashboard & admin polish",
    changes: [
      { type: "new", text: "Drag to reorder dashboard tabs — rearrange your tabs by dragging them into any order, saved automatically" },
      { type: "new", text: "Rename widgets from the dashboard — click the pencil icon on any tile to rename it and update its chart type or axes in one step" },
      { type: "improved", text: "Admin panel layout redesigned to match the settings page — vertical sidebar navigation replaces the horizontal tab bar" },
      { type: "new", text: "Integrations page added to admin — lists every external service the platform connects to, including Claude, Databricks, Power BI, Slack, ClickUp, SendGrid, and AWS S3" },
      { type: "fixed", text: "Dashboard widget buttons (refresh, edit, remove) could not be clicked because drag events were intercepting pointer interactions" },
      { type: "fixed", text: "Dashboard grid now uses the full available width instead of being constrained to a fixed pixel value" },
    ],
  },
  {
    version: "1.18.0",
    date: "2026-07-13",
    highlights: "Text-to-widget dashboard builder",
    changes: [
      { type: "new", text: "Ask Brian to add a widget — type what you want to see directly into the chat bar at the top of any dashboard and Brian will propose a matching widget with a live data preview before it's added" },
      { type: "new", text: "Widget proposals show a mini data preview table so you can confirm the data looks right before committing" },
      { type: "new", text: "Ambiguous requests trigger a single clarifying question rather than a best guess, keeping follow-up context for a natural back-and-forth" },
      { type: "new", text: "If the requested data doesn't exist in the warehouse Brian says so clearly rather than adding an empty or broken widget" },
      { type: "new", text: "Drag and drop tiles to reposition them anywhere on the dashboard — layout is saved automatically and restored on next visit" },
      { type: "new", text: "Resize tiles by dragging the bottom-right corner — make charts taller or wider to suit what they're showing" },
    ],
  },
  {
    version: "1.17.0",
    date: "2026-07-09",
    highlights: "Personal dashboards & audit log improvements",
    changes: [
      { type: "new", text: "Multiple named dashboards — create as many dashboards as you like, each with its own tab. Brian AI-generates 3 tailored tiles from a plain-English description when you create one" },
      { type: "new", text: "Full dashboard control — rename any dashboard with a double-click or pencil icon, and delete it when you no longer need it" },
      { type: "new", text: "Personalised greeting header — your Intelligence home now opens with a time-appropriate greeting, a live clock, and an environment badge showing whether you're on the live platform or staging" },
      { type: "improved", text: "Dashboards persist correctly across page refreshes — fixed a routing bug where the dashboard list would silently fail to load" },
      { type: "improved", text: "Audit log UI — richer event detail modal, improved action badges with icons, and consistent date formatting throughout the admin audit views" },
      { type: "improved", text: "Admin modals migrated to TypeScript with a shared Modal component and reusable hook, improving consistency across user management, subscriptions, and report actions" },
    ],
  },
  {
    version: "1.16.0",
    date: "2026-07-07",
    highlights: "Settings page & navigation redesign",
    changes: [
      { type: "new", text: "Settings is now a dedicated full page — Profile, Password, Security, Connections, and Dashboard setup each have their own section with a vertical nav on desktop and a hamburger menu on mobile" },
      { type: "new", text: "What's new and Sign out have moved into the Settings nav, keeping the main sidebar focused on navigation" },
      { type: "new", text: "Rerun the onboarding wizard any time from Settings → Dashboard setup to regenerate your starter dashboard" },
      { type: "improved", text: "Main sidebar footer is now a single row — your email address and a cog icon that links directly to Settings" },
      { type: "improved", text: "My Brian section added to the main sidebar — Intelligence home (your dashboard) is the default landing page after login" },
    ],
  },
  {
    version: "1.15.0",
    date: "2026-07-01",
    highlights: "Conversation & SQL audit log",
    changes: [
      { type: "new", text: "Conversation audit log — admins can view a full history of all conversations across every user, including message content and timestamps" },
      { type: "new", text: "SQL audit log — every query executed against the data warehouse is recorded and searchable, showing the SQL, the user who triggered it, and when it ran" },
      { type: "new", text: "Audit log admin UI — filterable and paginated tables for both logs are accessible from the Admin panel" },
    ],
  },
  {
    version: "1.14.0",
    date: "2026-06-25",
    highlights: "Onboarding wizard",
    changes: [
      { type: "new", text: "First-login onboarding wizard — new users see a full-screen prompt asking what they care about; Brian generates 3 personalised starter dashboard tiles from the description" },
      { type: "new", text: "Onboarding state persists — the wizard never shows again once completed and can be re-triggered from Settings → Dashboard setup" },
      { type: "new", text: "Starter dashboard tiles are pinned to your Intelligence home dashboard automatically after the wizard completes" },
    ],
  },
  {
    version: "1.13.0",
    date: "2026-06-15",
    highlights: "Chat answer feedback",
    changes: [
      { type: "new", text: "Answer feedback — thumbs up/down on every chat response lets you rate the quality of Brian's answers directly in conversation" },
      { type: "new", text: "Feedback attachments — when leaving feedback you can attach files or screenshots to give context alongside your rating" },
      { type: "new", text: "Feedback suggestions — the UI surfaces common feedback prompts to make it quicker to describe what went wrong" },
      { type: "new", text: "Feedback charts — admins can view rating trends over time in the Admin panel" },
      { type: "improved", text: "Accordion layout in chat responses — long structured answers collapse into expandable sections for easier reading" },
    ],
  },
  {
    version: "1.12.0",
    date: "2026-06-05",
    highlights: "CSV file upload & analysis",
    changes: [
      { type: "new", text: "CSV upload in chat — attach a CSV file (up to 10 MB, 5,000 rows) and ask Brian to analyse it directly without needing a warehouse query" },
      { type: "new", text: "Brian reads CSV data directly — uploaded files are analysed in the message context, not pushed through the SQL warehouse" },
      { type: "improved", text: "File attachment UI supports both CSV and image files from a single attach button" },
    ],
  },
  {
    version: "1.11.0",
    date: "2026-05-28",
    highlights: "ClickUp integration",
    changes: [
      { type: "new", text: "ClickUp integration — ask Brian to search for tasks, look up task details, or find work assigned to specific team members directly in chat" },
      { type: "new", text: "Workspace-wide search — task lookups cover all spaces in your ClickUp workspace, not just a single list" },
      { type: "improved", text: "ClickUp queries use the same natural-language interface as warehouse queries — no special syntax required" },
    ],
  },
  {
    version: "1.10.0",
    date: "2026-05-25",
    highlights: "Image attachments in chat",
    changes: [
      { type: "new", text: "Image attachments — upload images directly in the chat input for Brian to analyse, describe, or use as context in your question" },
      { type: "new", text: "Drag-and-drop — drag an image file anywhere onto the chat input area to attach it without clicking the upload button" },
    ],
  },
  {
    version: "1.9.0",
    date: "2026-05-20",
    highlights: "AI Analysis & Recommendations tiles",
    changes: [
      { type: "new", text: "AI Analysis tile — automatically summarises all other tiles' data each snapshot run, calling out key trends, notable figures, and anomalies" },
      { type: "new", text: "AI Recommendations tile — generates 3–5 specific, actionable recommendations based on the snapshot data to help improve business performance" },
      { type: "new", text: "Both insight tiles support an optional 'Focus instructions' field to guide the AI's attention toward specific areas" },
      { type: "improved", text: "Insight tiles display with distinct coloured borders (blue for analysis, amber for recommendations) and an AI-generated badge" },
    ],
  },
  {
    version: "1.8.0",
    date: "2026-05-20",
    highlights: "Tile widths, currency display, smarter SQL generation",
    changes: [
      { type: "new", text: "Third-width and quarter-width tile options — tile builder now supports four widths: quarter (1/4), third (1/3), half (1/2), and full" },
      { type: "new", text: "Currency flag on tiles — mark a KPI tile as currency to display values with a £ symbol and 2 decimal places" },
      { type: "improved", text: "SQL agent now returns all sites/brands by default (using GROUP BY) unless the description explicitly filters to one" },
      { type: "improved", text: "SQL agent combines sportsbook and casino data by default — both products are included unless the description specifies only one" },
    ],
  },
  {
    version: "1.7.0",
    date: "2026-05-20",
    highlights: "Notifications inbox + smarter dashboard tiles",
    changes: [
      { type: "new", text: "Notifications inbox — bell icon in the top bar shows unread count; dashboard run results (success, partial, failed) trigger automatic notifications for relevant users" },
      { type: "new", text: "Generate SQL with agent — describe a tile in plain English and the agent writes the SQL against your schema, no copy-pasting from chat required" },
      { type: "new", text: "KPI delta display — number tiles automatically show a ↑/↓ % change badge when the SQL returns a second numeric column as a comparison value" },
      { type: "improved", text: "Run Now shows live status — button updates to 'Running…', then 'Done ✓', 'Partial ⚠', or 'Failed ✗' with an 8-second result banner" },
    ],
  },
  {
    version: "1.6.0",
    date: "2026-05-19",
    highlights: "Scheduled shared dashboards",
    changes: [
      { type: "new", text: "Scheduled dashboards — super admins build dashboards of SQL tiles that run automatically on a daily schedule (e.g. 5am UTC) and store a snapshot of the results" },
      { type: "new", text: "Snapshot history — users can navigate back through previous days' data using the snapshot navigator" },
      { type: "new", text: "Sharing controls — dashboards can be shared with all users or restricted to specific team members" },
      { type: "new", text: "Tile builder — supports KPI number, bar chart, line chart, and table displays with configurable SQL, axes, and width (half/full)" },
      { type: "new", text: "Test SQL — preview tile data before saving directly in the admin builder" },
      { type: "new", text: "Run now — admins can trigger a snapshot on demand from the Admin panel" },
    ],
  },
  {
    version: "1.5.0",
    date: "2026-05-19",
    highlights: "Expanded admin settings",
    changes: [
      { type: "new", text: "Notifications settings — super admins can configure Slack channel IDs and email sender name directly from the Admin panel without a deployment" },
      { type: "improved", text: "Settings tab reorganised into sections: Agent prompt customisation and Notifications, each with independent save controls" },
      { type: "improved", text: "Slack channel IDs set in the UI override the SLACK_CHANNEL_IDS environment variable and take effect within 60 seconds" },
    ],
  },
  {
    version: "1.4.0",
    date: "2026-05-19",
    highlights: "Agent prompt customisation",
    changes: [
      { type: "new", text: "Agent prompt customisation — super admins can add custom business instructions to the agent's system prompt directly from the Admin panel, no deployment needed" },
      { type: "new", text: "Changes take effect within 60 seconds — set currency preferences, naming conventions, default datasources, or any standing instruction for every conversation" },
      { type: "improved", text: "Live preview shows exactly what the agent will see before saving" },
    ],
  },
  {
    version: "1.3.0",
    date: "2026-05-19",
    highlights: "Usage analytics",
    changes: [
      { type: "new", text: "Usage analytics — admins can view message volume, active users, and token usage over 7, 30, or 90 days with daily line and bar charts" },
      { type: "new", text: "Per-user breakdown table — sortable by messages, conversations, tokens used, or last active date" },
      { type: "new", text: "Token split — headline KPIs show total tokens with input/output breakdown and tokens-per-message average" },
    ],
  },
  {
    version: "1.2.0",
    date: "2026-05-19",
    highlights: "Dashboard page",
    changes: [
      { type: "new", text: "Dashboard page — pin any saved report to your personal dashboard as a chart tile (bar, line, KPI number, or table)" },
      { type: "new", text: "Dashboard tiles auto-run their SQL query and refresh on demand — unpin or reconfigure any tile directly from the dashboard" },
      { type: "improved", text: "Reports page now shows an 'On dashboard' badge and a pin icon for each report with a SQL query" },
    ],
  },
  {
    version: "1.1.0",
    date: "2026-05-18",
    highlights: "Anomaly alerts",
    changes: [
      { type: "new", text: "Anomaly alerts — admins can create alert rules that run a SQL query every 15 minutes and send email or Slack notifications when the result crosses a threshold" },
      { type: "new", text: "Alert firing history — view a log of every time each rule fired, with the triggered value and timestamp" },
    ],
  },
  {
    version: "1.0.0",
    date: "2026-05-19",
    highlights: "Initial versioned release",
    changes: [
      { type: "new", text: "Comparison mode — ask the agent to compare two time periods or segments and get a side-by-side table with % change and chart" },
      { type: "new", text: "Multi-turn memory — the agent now remembers context within a conversation (date ranges, filters, last query) so follow-up questions like 'same as last time but for this week' work reliably" },
      { type: "new", text: "Trending questions — admins can view the most frequently asked questions and pin them as suggested questions in the chat" },
      { type: "new", text: "Power BI integration — connect your Power BI account to push query results as datasets or browse and embed your existing reports directly in chat" },
      { type: "new", text: "Mobile-responsive layout — sidebar drawer, stacked buttons, and scrollable modals on small screens" },
      { type: "improved", text: "SQL queries are hidden by default — Download and Save as Report actions appear as small links; click Show SQL to reveal the query" },
      { type: "improved", text: "Download results from chat — CSV and JSON export now works correctly for all queries including those using CTEs and column names containing keywords like 'updated'" },
      { type: "fixed", text: "Profile settings modal now opens full-screen on desktop instead of being constrained to the sidebar" },
    ],
  },
];

export default changelog;
